/**
 * Formats a duration in milliseconds to a compact human-readable string.
 * Uses the largest appropriate unit: days > hours > minutes > seconds >
 * milliseconds. Aggregate durations (a project's total active time, a monthly
 * Stats bucket) routinely run to hundreds of hours, which read poorly as
 * "734.5h" — the days tier keeps them glanceable ("30.6d").
 * @param ms Duration in milliseconds (can be string or number)
 * @returns Formatted string like "30s", "5.2m", "2.1h", or "3.4d"
 */
export function formatDuration(ms: string | number): string {
  const num = typeof ms === 'string' ? parseInt(ms, 10) : ms;
  if (isNaN(num) || num < 0) return '0s';
  if (num === 0) return '0s';

  // For very short durations, show milliseconds
  if (num < 1000) return `${num}ms`;

  // Calculate total in each unit for decimal display
  const totalSeconds = num / 1000;
  const totalMinutes = totalSeconds / 60;
  const totalHours = totalMinutes / 60;
  const totalDays = totalHours / 24;

  // Show the most appropriate unit with decimals. Note `.toFixed(1)` rounds at
  // the 23.95h boundary, so 23.95h–23.99h renders "24.0h" rather than
  // promoting to "1.0d" — readable either way, same glanceable one-decimal
  // style the smaller units already use.
  if (totalDays >= 1) {
    return `${totalDays.toFixed(1)}d`;
  }
  if (totalHours >= 1) {
    return `${totalHours.toFixed(1)}h`;
  }
  if (totalMinutes >= 1) {
    return `${totalMinutes.toFixed(1)}m`;
  }
  return `${totalSeconds.toFixed(1)}s`;
}

/**
 * Formats a Unix timestamp (in seconds as string) to a relative time string.
 * @param timestamp Unix timestamp in seconds (as string from API)
 * @returns Formatted string like "2 minutes ago", "3 hours ago", or full date
 */
export function formatTimestamp(timestamp: string): string {
  const ts = parseInt(timestamp, 10);
  if (isNaN(ts) || ts === 0) return '—';

  const now = Date.now();
  const date = new Date(ts * 1000);
  const diffMs = now - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Less than a minute ago
  if (diffSeconds < 60) {
    return 'just now';
  }

  // Less than an hour ago
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  }

  // Less than a day ago
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  }

  // Less than a week ago
  if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  }

  // Show full date for older timestamps
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Formats a number with K/M/B notation for large values.
 * @param num Number to format (can be string or number)
 * @returns Formatted string like "1.2K", "3.5M", or "42"
 */
export function formatNumber(num: string | number): string {
  const n = typeof num === 'string' ? parseInt(num, 10) : num;
  if (isNaN(n)) return '0';
  if (n === 0) return '0';

  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);

  // Each unit's effective floor is 999.95× (not 1000×) because `.toFixed(1)`
  // rounds 999.95 up to "1000.0" — so 999,999 must promote to "1.0M", not the
  // malformed "1000.0K" (same for the M→B boundary).
  if (abs >= 999_950_000) return sign + compactUnit(abs / 1_000_000_000, 'B');
  if (abs >= 999_950) return sign + compactUnit(abs / 1_000_000, 'M');
  if (abs >= 999.95) return sign + compactUnit(abs / 1_000, 'K');
  return n.toString();
}

// Renders a scaled magnitude as an integer when whole, else one decimal place,
// suffixed with its unit (e.g. 25 → "25K", 1.5 → "1.5K").
function compactUnit(value: number, suffix: string): string {
  return (value % 1 === 0 ? `${value}` : value.toFixed(1)) + suffix;
}

/**
 * Formats an integer with thousands separators (e.g. 1193 → "1,193").
 * Use for precise counts in detail views; use formatNumber for glanceable
 * K/M/B summaries.
 */
export function formatInt(num: string | number): string {
  const n = typeof num === 'string' ? parseInt(num, 10) : num;
  if (isNaN(n)) return '0';
  return n.toLocaleString('en-US');
}

/**
 * Formats a decimal as a percentage.
 * @param ratio Decimal ratio (0.0 to 1.0)
 * @param decimals Number of decimal places (default 1)
 * @returns Formatted string like "42.5%"
 */
export function formatPercentage(ratio: number, decimals: number = 1): string {
  if (isNaN(ratio) || ratio < 0) return '0%';
  const percent = ratio * 100;
  return `${percent.toFixed(decimals)}%`;
}

/**
 * Normalizes a markdown numbered list so items aren't split across multiple
 * <ol> elements. Fixes cases where the LLM produces a bare number+period on
 * one line with the content on the next line (e.g. "2.\nContent…"), which
 * CommonMark parsers render as an empty <li> followed by a stray <p>.
 */
export function normalizeNumberedList(markdown: string): string {
  // 1. Join bare list markers with the following line:
  //    "2.\n  Content…" → "2. Content…"
  let result = markdown.replace(/^(\d+)\.\s*\n(?!\s*\d+\.\s)/gm, '$1. ');

  // 2. Remove blank lines between consecutive list items so the parser
  //    treats them as a single list instead of multiple <ol> elements.
  result = result.replace(/^(\d+\..+)\n\n+(?=\d+\.\s)/gm, '$1\n');

  return result;
}

/**
 * Returns the singular or plural form of a noun for the given count. Pass an
 * explicit `plural` for irregular nouns; otherwise the singular is suffixed with
 * "s". Returns only the noun word (not the count) so callers keep control of
 * layout, e.g. `${n} ${pluralize(n, 'session')}` → "1 session" / "2 sessions".
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : plural ?? `${singular}s`;
}

export function formatAutonomy(score?: number): string {
  if (score === undefined || score === null) return '-';
  if (!score && score !== 0) return '-';
  return `${(score * 100).toFixed(0)}%`;
}

/**
 * Formats a Unix timestamp (seconds) to a locale date/time string.
 * @param timestamp Unix timestamp in seconds (string or number)
 * @returns Formatted date string or 'Unknown'
 */
export function formatDate(timestamp: string | number): string {
  const ts = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
  if (!ts || isNaN(ts)) return 'Unknown';
  return new Date(ts * 1000).toLocaleString();
}

/**
 * Short clock time (e.g. "2:04 PM") from a unix-seconds timestamp — for the
 * timestamp column of the event log, where the full date would be redundant.
 * Returns '' for invalid input so the cell renders empty rather than "Unknown".
 */
export function formatClockTime(timestamp: string | number): string {
  const ts = typeof timestamp === 'string' ? parseInt(timestamp, 10) : timestamp;
  if (!ts || isNaN(ts)) return '';
  return new Date(ts * 1000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

// formatUsd renders a dollar amount compactly: cents below $100, whole
// dollars (with thousands separators) above.
export function formatUsd(usd: number): string {
  if (!isFinite(usd)) return '$0';
  if (usd < 100) return `$${usd.toFixed(2)}`;
  return `$${Math.round(usd).toLocaleString('en-US')}`;
}

/**
 * Formats a "N×" multiplier glanceably — one decimal below 10, whole number
 * at/above (mirroring the hours style in TimeLedgerCard). The key honesty
 * rule: a *positive* ratio must never collapse to a deceptive "0×" the way a
 * plain `.toFixed(0)` does — $30 of value against a $100 plan fee is "0.3×",
 * not "0×" (which reads as "produced nothing"). A ratio small enough to round
 * to "0.0×" floors to "<0.1×" instead (the same floor-sentinel pattern as the
 * Projects friction burden). Only a genuine zero (or non-finite) renders "0×".
 * @returns e.g. "0×", "<0.1×", "0.3×", "2.7×", "12×"
 */
export function formatMultiplier(ratio: number): string {
  if (!isFinite(ratio) || ratio <= 0) return '0×';
  if (ratio < 0.05) return '<0.1×';
  // Round up to the whole-number tier at 9.95 so a value that would render
  // "10.0×" promotes to "10×" (the glanceable-formatter boundary rule).
  if (ratio >= 9.95) return `${Math.round(ratio)}×`;
  return `${ratio.toFixed(1)}×`;
}
